import * as HTMLElementCreator from '../../libs/HTML/HTMLElementCreator';
import { BUTTON_STYLE, INPUT_STYLE, LABEL_STYLE } from './EditorOverlayStyles';
import { EditMode } from '../types';
import { BOARD_DEFAULT_SIZE } from '../constants';
import { createActionDialog } from '../../libs/HTML/HTMLElementCreator';

const modeButtonsConfigs: ModeButtonConfig[] = [
  { label: 'Level Properties', value: EditMode.Default },
  { label: '+/- Stacks', value: EditMode.ToggleStacks },
  { label: '+/- Tiles', value: EditMode.EditTiles },
  { label: '+/- Blockers', value: EditMode.PaintBlockers },
  { label: '+/- UT Blockers', value: EditMode.PaintUnderTileBlockers },
  { label: 'Remix', value: EditMode.Remix },
];

interface ModeButtonConfig {
  label: string;
  value: EditMode;
}

interface ModeButton {
  element: HTMLElement;
  config: ModeButtonConfig;
}

export interface EditorHeaderConfig {
  readonly onNewBoard: (width: number, height: number) => void;
  readonly onUndo: () => void;
  readonly onModeChange: (value: EditMode) => void;
}

export class EditorHeader {
  public readonly element: HTMLElement;

  private readonly modeButtons: ModeButton[];

  constructor({ onNewBoard, onUndo, onModeChange }: EditorHeaderConfig) {
    this.element = HTMLElementCreator.createDiv({
      elementID: 'editor-header-panel',
      parentElement: undefined,
      style: {
        position: 'fixed',
        top: '5px',
        right: '5px',
        color: 'black',
        padding: '2px',
        backgroundColor: 'rgba(0,0,0,0.25)',
        pointerEvents: 'auto',
      },
    });

    this.modeButtons = this.createModeButtons(onModeChange);
    this.createUndoButton(onUndo);
    this.createNewBoardPanel(onNewBoard);
  }

  public destroy(): void {
    this.element.remove();
  }

  private createModeButtons(onModeChange: (value: EditMode) => void): ModeButton[] {
    const panel = HTMLElementCreator.createDiv({
      parentElement: this.element,
      style: { display: 'inline' },
    });

    // Create EditorMode Label / Buttons.
    HTMLElementCreator.createLabel({
      parentElement: panel,
      style: { ...LABEL_STYLE },
      labelText: 'Modes:',
    });

    return modeButtonsConfigs.map((config) => this.createModeButton(panel, config, onModeChange));
  }

  private createModeButton(
    panel: Element,
    config: ModeButtonConfig,
    onModeChange: (value: EditMode) => void,
  ): ModeButton {
    const { label, value } = config;
    const element = HTMLElementCreator.createButton({
      parentElement: panel,
      labelText: label,
      onClick: () => {
        this.highlightEditorMode(value);
        onModeChange(value);
      },
      style: { ...BUTTON_STYLE },
    });
    return { element, config };
  }

  public highlightEditorMode(value: EditMode): void {
    if (value === EditMode.EditBlockerConnections) {
      this.modeButtons.forEach((button) => {
        button.element.style.backgroundColor = BUTTON_STYLE.backgroundColor;
      });

      const blockerButton = this.modeButtons.find(({ config }) => config.value === EditMode.PaintBlockers);
      if (blockerButton) {
        blockerButton.element.style.backgroundColor = '#4FC1FF';
      }

      return;
    }

    this.modeButtons.forEach(({ element, config }) => {
      element.style.backgroundColor = config.value === value ? '#00FF00' : BUTTON_STYLE.backgroundColor;
    });
  }

  private createUndoButton(onUndo: () => void): HTMLElement {
    return HTMLElementCreator.createButton({
      parentElement: this.element,
      labelText: 'Undo',
      onClick: () => {
        onUndo();
      },
      style: { ...BUTTON_STYLE, marginRight: '5px', marginLeft: '10px', minWidth: '60px' },
    });
  }

  private createNewBoardPanel(onNewBoard: (width: number, height: number) => void): HTMLElement {
    const panel = HTMLElementCreator.createDiv({
      parentElement: this.element,
      style: { display: 'inline' },
    });

    // Cols Label / Input.
    HTMLElementCreator.createLabel({
      parentElement: panel,
      style: { ...LABEL_STYLE },
      labelText: 'cols:',
    });

    const widthInput = HTMLElementCreator.createInputText({
      parentElement: panel,
      restrictRegEx: /^\d+$/,
      style: { ...INPUT_STYLE },
    });
    widthInput.value = BOARD_DEFAULT_SIZE.cols.toString();
    widthInput.maxLength = 2;

    // Rows Label / Input.
    HTMLElementCreator.createLabel({
      parentElement: panel,
      style: { ...LABEL_STYLE },
      labelText: 'rows:',
    });

    const heightInput = HTMLElementCreator.createInputText({
      parentElement: panel,
      restrictRegEx: /^\d+$/,
      style: { ...INPUT_STYLE },
    });
    heightInput.value = BOARD_DEFAULT_SIZE.rows.toString();
    heightInput.maxLength = 2;

    // New Board button.
    HTMLElementCreator.createButton({
      parentElement: panel,
      labelText: 'New',
      onClick: () => {
        createActionDialog({
          onConfirm: () => {
            const width = parseInt(widthInput.value, 10);
            const height = parseInt(heightInput.value, 10);
            onNewBoard(width, height);
          },
          titleText: 'Are you sure want to create a new board?',
          zIndex: Number.MAX_SAFE_INTEGER,
        });
      },
      style: { ...BUTTON_STYLE, marginLeft: '4px', minWidth: '60px' },
    });

    return panel;
  }
}
